import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, from, map, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EncrypDepcrypService } from '../_services';
import { AlertifyService } from '../_services/common/alertify.service';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
    private sessionRefreshPromise: Promise<void> | null = null;

    constructor(
        private encryptionService: EncrypDepcrypService,
        private alertifyService: AlertifyService
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (!request.url.includes('ipinfo')) {
            request = request.clone({ withCredentials: true });
        }

        // Only encrypt if required
        if (!this.shouldEncryptRequest(request)) {
            return next
                .handle(request)
                .pipe(
                    catchError((error: HttpErrorResponse) =>
                        this.handleError(error)
                    )
                );
        }

        // Ensure keys are initialized before encrypting
        if (!this.encryptionService.isKeyPresent()) {
            return from(this.encryptionService.initialize()).pipe(
                switchMap(() => this.handleEncryptedRequest(request, next))
            );
        }

        return this.handleEncryptedRequest(request, next);
    }

    private handleEncryptedRequest(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const encryptedRequest = this.createEncryptedRequest(request);

        return next.handle(encryptedRequest).pipe(
            map((event: HttpEvent<any>) => this.decryptResponse(event)),
            catchError((error: HttpErrorResponse) =>
                this.handleRequestError(error, request, next)
            )
        );
    }

    private handleRequestError(
        error: HttpErrorResponse,
        originalRequest: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (this.isSessionExpired(error)) {
            return this.retryWithNewSession(originalRequest, next);
        }
        return this.handleError(error);
    }

    private retryWithNewSession(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (!this.sessionRefreshPromise) {
            this.sessionRefreshPromise = this.encryptionService
                .initialize()
                .finally(() => {
                    this.sessionRefreshPromise = null;
                });
        }

        return from(this.sessionRefreshPromise).pipe(
            switchMap(() => this.handleEncryptedRequest(request, next)),
            catchError((error) => {
                return throwError(() => error);
            })
        );
    }

    private isSessionExpired(error: HttpErrorResponse): boolean {
        return (
            error.status === 403 &&
            (error.error === 'SESSION_EXPIRED' ||
                error.error === 'Invalid or expired session key' ||
                error.error?.includes('expired session'))
        );
    }

    private shouldEncryptRequest(request: HttpRequest<any>): boolean {
        return (
            environment.isCloud &&
            !request.url.includes('Documents/') &&
            !request.url.includes('Auth/PublicKey') &&
            !request.url.includes('Auth/register-key') &&
            !request.url.includes('assets/') &&
            !request.url.includes('ipinfo') &&
            !request.url.includes('logincheckhub') &&
            !request.url.includes('GetJasperReportFile')
        );
    }

    private createEncryptedRequest(
        request: HttpRequest<any>
    ): HttpRequest<any> {
        const [baseUrl, apiUrl] = request.url.split('api/');
        const encryptedUrl = this.encryptionService.encrypt(apiUrl);

        let clonedRequest = request.clone({
            url: `${baseUrl}api/${encryptedUrl}`,
            setHeaders: {
                'X-Session-Id': this.encryptionService.getSessionId(),
            },
        });

        if (['POST', 'PUT', 'PATCH'].includes(request.method)) {
            clonedRequest =
                request.body instanceof FormData
                    ? this.encryptFormData(request, clonedRequest)
                    : clonedRequest.clone({
                          body: this.encryptionService.encryptBody(
                              request.body
                          ),
                      });
        }

        return clonedRequest;
    }

    private encryptFormData(
        request: HttpRequest<any>,
        clonedRequest: HttpRequest<any>
    ): HttpRequest<any> {
        const formData = new FormData();
        request.body.forEach((value: any, key: string) => {
            formData.append(
                key,
                value instanceof File
                    ? value
                    : this.encryptionService.encrypt(value)
            );
        });
        return clonedRequest.clone({ body: formData });
    }

    private decryptResponse(event: HttpEvent<any>): HttpEvent<any> {
        if (event instanceof HttpResponse && event.body) {
            const decryptedBody = this.encryptionService.decrypt(
                event.body.response
            );

            try {
                const parsedBody = JSON.parse(decryptedBody);
                return event.clone({ body: parsedBody });
            } catch (e) {
                return event.clone({ body: decryptedBody });
            }
        }
        return event;
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        switch (error.status) {
            case 500:
                this.alertifyService.error('500 Internal Server Error');
                break;
            case 415:
                this.alertifyService.error('Unsupported Media Type');
                break;
            default:
            //console.log('An HTTP error occurred', error);
        }
        return throwError(() => error);
    }
}
